import { onDomReady } from '../utils/dom-ready';

const SUBMIT_ENDPOINT = '/.netlify/functions/rsvp';
const REVIEW_STEP = 5;
const MAX_GUESTS = 3;

const data = {};

/**
 * Find an element by container name.
 *
 * @param {String} name Name of element
 * @returns {Node}
 */
function findContainer(name) {
  return document.querySelector(`[data-rsvp-container="${name}"]`);
}

/**
 * Find an element by action name.
 *
 * @param {String} name Name of action
 * @returns {Node}
 */
function findAction(name) {
  return document.querySelector(`[data-rsvp-action="${name}"]`);
}

/**
 * Show an element which has been hidden.
 *
 * @param {Node} element Element to show
 * @returns {undefined}
 */
function showElement(element) {
  element.removeAttribute('hidden');
}

/**
 * Hide an element.
 *
 * @param {Node} element Element to hide
 * @returns {undefined}
 */
function hideElement(element) {
  element.setAttribute('hidden', 'true');
}

function hideForm() {
  const stepElements = document.querySelectorAll('[data-rsvp-step]');
  stepElements.forEach((stepElement) => hideElement(stepElement));
}

function showSpinner() {
  hideForm();
  showElement(findContainer('spinner'));
}

function hideSpinner() {
  hideElement(findContainer('spinner'));
}

function showSuccessYes() {
  showElement(findContainer('success-yes'));
}

function showSuccessNo() {
  showElement(findContainer('success-no'));
}

function showError() {
  showElement(findContainer('error'));
}

function hideError() {
  hideElement(findContainer('error'));
}

/**
 * Show a form step.
 *
 * @param {Number} currentStep Form step to show
 * @returns {undefined}
 */
function showFormStep(currentStep) {
  // Allow going through all the steps again during revision.
  if (currentStep === 1) data.attending = 'yes';

  const currentStepElement = document.querySelector(
    `[data-rsvp-step="${currentStep}"]`
  );

  if (!currentStepElement) return;

  const otherStepElements = document.querySelectorAll(
    `[data-rsvp-step]:not([data-rsvp-step="${currentStep}"])`
  );

  hideError();
  otherStepElements.forEach(hideElement);
  showElement(currentStepElement);

  if (currentStep > 1) {
    currentStepElement.scrollIntoView();
    const firstInput = currentStepElement.querySelector('input, textarea');
    if (firstInput) firstInput.focus();
  }
}

/**
 * Check if the current step is the last step.
 *
 * @param {Number} currentStep Step to check if it's last
 * @returns {Boolean}
 */
function isLastStep(currentStep) {
  const lastStep = document.querySelectorAll('[data-rsvp-step]').length;
  return currentStep === lastStep;
}

/**
 * Save current form fields to `data`.
 *
 * @param {FormData} formData Current form data
 * @returns {undefined}
 */
function setData(formData) {
  for (const name of formData.keys()) {
    data[name] = formData.get(name);
  }
}

/**
 * Render review screen.
 *
 * @param {Object} reviewData Data to render
 * @returns {undefined}
 */
function renderReview(reviewData) {
  const renderGuest = (position) => {
    const name = reviewData[`guest_name_${position}`];
    const email = reviewData[`guest_email_${position}`];

    if (!name) return '';

    return `
      <p class="${position !== MAX_GUESTS ? 'spacing--1' : ''}">
        <strong>${name}</strong>${email ? `(${email})` : ''}
      </p>
    `;
  };

  if (reviewData.attending === 'no') {
    findContainer('review').innerHTML = `
      <div>
        <h2 class="spacing--1">${reviewData.name}</h2>
        <p>Not Attending</p>
      </div>
    `;
    return;
  }

  findContainer('review').innerHTML = `
    <div class="spacing--4">
      <h2 class="spacing--1">All Guests</h2>

      ${
        data.name
          ? `
        <p class="spacing--1">
          <strong>${reviewData.name}</strong> ${
              reviewData.email ? `(${reviewData.email})` : ''
            }
        </p>
      `
          : 'No Primary'
      }

      ${Array.from(Array(MAX_GUESTS))
        .map((_, index) => renderGuest(index + 1))
        .join('')}
    </div>

    <div class="spacing--4">
      <h2 class="spacing--1">Dietary Restrictions</h2>
      <p>${reviewData.dietary_restrictions || 'None'}</p>
    </div>
  `;
}

function handleSubmit(event) {
  event.preventDefault();

  const currentStep = parseInt(event.target.getAttribute('data-rsvp-step'));
  const nextStep = currentStep + 1;

  setData(new FormData(event.target));
  renderReview(data);

  if (isLastStep(currentStep)) {
    showSpinner();

    fetch(SUBMIT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
      .then((response) => {
        hideSpinner();

        if (!response.ok) {
          showFormStep(1);
          showError();
          return;
        }

        if (data.attending === 'yes') showSuccessYes();
        if (data.attending === 'no') showSuccessNo();
      })
      .catch((error) => {
        showFormStep(1);
        showError();
      });

    return;
  }

  if (data.attending === 'no') {
    showFormStep(REVIEW_STEP);
    return;
  }

  showFormStep(nextStep);
}

let guestsCount = 1;
function handleAddGuest(event) {
  guestsCount += 1;

  const guestsContainer = findContainer('guests');
  const html = guestsContainer.outerHTML
    .replace('guest_name_1', `guest_name_${guestsCount}`)
    .replace('guest_email_1', `guest_email_${guestsCount}`);

  event.target.insertAdjacentHTML('beforebegin', html);

  const newGuestName = document.querySelector(
    `input[name="guest_name_${guestsCount}"]`
  );

  newGuestName.focus();

  if (guestsCount === MAX_GUESTS) event.target.remove();
}

onDomReady(() => {
  hideSpinner();

  const stepElements = document.querySelectorAll('[data-rsvp-step]');
  stepElements.forEach((stepElement) =>
    stepElement.addEventListener('submit', handleSubmit)
  );

  findAction('add-guest').addEventListener('click', handleAddGuest);
  findAction('edit').addEventListener('click', () => showFormStep(1));

  showFormStep(1);
});
